import { useMediaQuery } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import { SIDEBAR_BREAKPOINT_PX } from '@/shared/config';

// Mobile/desktop по общему breakpoint — тот же медиазапрос, что в `useIsMobile()`, но без
// импорта `@/shared/lib` на уровне модуля (его целиком мокают спеки страниц). Один
// экземпляр на модуль: состояние сайдбара общее для всех компонентов слайса.
const isDesktop = useMediaQuery(`(min-width: ${SIDEBAR_BREAKPOINT_PX}px)`);
const isMobile = computed(() => !isDesktop.value);

// Состояние для mobile (полноэкранная шторка меню)
const isMobileMenuOpen = ref<boolean>(false);

// Уровень 2 мобильной шторки: открытая группа, `null` — сетка разделов
const mobileGroupId = ref<string | null>(null);

/**
 * Группа, чья панель закреплена текущим маршрутом. Единственный писатель —
 * `AppSidebar.vue` (watch по route). Состояние живёт только в памяти: после
 * перезагрузки панель заново выводится из маршрута.
 */
const pinnedGroupId = ref<string | null>(null);

// Группа, показанная временным preview-оверлеем (клик по чужой иконке рейла).
// Переход по маршруту закрывает его — watch в `AppSidebar.vue`.
const previewGroupId = ref<string | null>(null);

// Свёрнута ли закреплённая панель. Держится внутри раздела, сбрасывается при смене раздела.
const isPanelCollapsed = ref<boolean>(false);

// При переходе на desktop автоматически закрываем mobile menu
watch(isMobile, (mobile) => {
  if (!mobile) {
    isMobileMenuOpen.value = false;
  }
});

// Смена раздела разворачивает панель: пользователь пришёл в новую группу — показываем её целиком.
watch(pinnedGroupId, () => {
  isPanelCollapsed.value = false;
});

/**
 * Ширина, на которую сайдбар сдвигает контент, — CSS-выражение из переменных
 * `--sidebar-*` (размеры живут только в `variables.css`). Preview — оверлей поверх
 * контента, в смещение не входит: страница под ним не «прыгает».
 */
const sidebarOffset = computed<string>(() => {
  if (pinnedGroupId.value === null) {
    return 'var(--sidebar-rail-width)';
  }

  const panelWidth = isPanelCollapsed.value ? '--sidebar-panel-collapsed-width' : '--sidebar-panel-width';

  return `calc(var(--sidebar-rail-width) + var(${panelWidth}))`;
});

export function useSidebar() {
  /** Открыть мобильную шторку (бургер в header) — всегда с уровня 1 */
  const openMobileMenu = () => {
    if (isMobile.value) {
      isMobileMenuOpen.value = true;
      mobileGroupId.value = null;
    }
  };

  /** Закрыть мобильную шторку (клик по пункту, смена route, backdrop, Esc) */
  const closeMobileMenu = () => {
    isMobileMenuOpen.value = false;
  };

  /** Открыть уровень 2 мобильной шторки (список пунктов группы) */
  const openMobileGroup = (id: string) => {
    mobileGroupId.value = id;
  };

  /** Вернуться на уровень 1 мобильной шторки (сетка разделов) */
  const closeMobileGroup = () => {
    mobileGroupId.value = null;
  };

  /** Скрыть preview-оверлей */
  const closePreview = () => {
    previewGroupId.value = null;
  };

  /** Свернуть/развернуть закреплённую панель */
  const togglePanelCollapsed = () => {
    isPanelCollapsed.value = !isPanelCollapsed.value;
  };

  /**
   * Клик по иконке группы в рейле. По своей текущей группе — разворачиваем панель
   * (в свёрнутом состоянии это единственный способ вернуть её), по чужой — preview.
   */
  const handleRailGroupClick = (id: string) => {
    if (id === pinnedGroupId.value) {
      isPanelCollapsed.value = false;
      previewGroupId.value = null;

      return;
    }

    previewGroupId.value = id;
  };

  return {
    // Состояние
    isMobile,
    isMobileMenuOpen,
    mobileGroupId,
    pinnedGroupId,
    previewGroupId,
    isPanelCollapsed,
    sidebarOffset,

    // Методы
    openMobileMenu,
    closeMobileMenu,
    openMobileGroup,
    closeMobileGroup,
    closePreview,
    togglePanelCollapsed,
    handleRailGroupClick,
  };
}
